import { useCallback, useState, type Dispatch, type SetStateAction } from 'react';
import type { ExpenseRegisterSubmitInput } from '../expenseRegisterSubmitTypes';
import { parseDecimalInput } from '../../../shared/numeric/localeNumeric';
import { showError, showInfo, showSuccess } from '../../../shared/ui/appToast';
import { getErrorMessage } from '../../../shared/errorMessage';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import type { OperationalConfirmOptions } from '../../../shared/ui/useOperationalConfirm';
import { invalidateCashSummaryCaches } from '../../../cash/infra-api';
import { formatMoney } from '../../../shared/format/public-api';
import {
  executeExpenseRegister,
  expenseRegisterSuccessCopy,
  shouldConfirmCashDrawerOverExpected,
  validateExpenseRegisterBusinessRules,
} from '../submitExpenseRegister';

type Params = {
  canEdit: boolean;
  organizationId: string | null;
  branchId: string | null;
  activeCashSessionId: string | null;
  userId: string | null | undefined;
  expectedCashInDrawer?: number | null;
  onSuccess?: () => void | Promise<void>;
  onClose?: () => void;
  setSubmitting?: Dispatch<SetStateAction<boolean>>;
  confirm?: (options: OperationalConfirmOptions) => Promise<boolean>;
};

export function useExpenseRegisterSubmit({
  canEdit,
  organizationId,
  branchId,
  activeCashSessionId,
  userId,
  expectedCashInDrawer,
  onSuccess,
  onClose,
  setSubmitting: externalSetSubmitting,
  confirm,
}: Params) {
  const [internalSubmitting, setInternalSubmitting] = useState(false);
  const setSubmitting = externalSetSubmitting ?? setInternalSubmitting;

  const runSubmit = useCallback(
    async (input: ExpenseRegisterSubmitInput, amount: number) => {
      if (!canEdit || !branchId || !organizationId) return;

      setSubmitting(true);
      try {
        const result = await executeExpenseRegister(
          input,
          {
            organizationId,
            branchId,
            activeCashSessionId,
            createdBy: userId ?? null,
          },
          amount
        );
        onClose?.();
        const copy = expenseRegisterSuccessCopy(input.paymentIntent);
        showSuccess(copy.title, copy.message);
        if (result.attachmentFailed) {
          showInfo(
            'Comprobante',
            'La factura quedó, pero no pude guardar el archivo. Adjuntálo desde el detalle.'
          );
        }
        invalidateCashSummaryCaches(branchId);
        await onSuccess?.();
      } catch (error: unknown) {
        presentUnexpectedFailure({ module: 'expenses', op: 'expense.register', error, userMessage: 'No se pudo registrar el gasto.' });
      } finally {
        setSubmitting(false);
      }
    },
    [canEdit, branchId, organizationId, activeCashSessionId, userId, onClose, onSuccess, setSubmitting]
  );

  const handleSubmit = useCallback(
    async (input: ExpenseRegisterSubmitInput) => {
      if (!canEdit || !branchId || !organizationId) return;

      const amount = parseDecimalInput(input.amountText, { min: 0.01 });
      if (amount == null) {
        showError('Monto inválido', 'Ingresá un monto mayor a cero.');
        return;
      }

      const businessError = validateExpenseRegisterBusinessRules(input, { activeCashSessionId }, amount);
      if (businessError) {
        showError(businessError.title, businessError.message);
        return;
      }

      const proceed = () => {
        void runSubmit(input, amount);
      };

      if (
        confirm &&
        shouldConfirmCashDrawerOverExpected(input) &&
        expectedCashInDrawer != null &&
        amount > expectedCashInDrawer
      ) {
        const ok = await confirm({
          title: 'Sale más plata de la que hay en caja',
          message: `Querés sacar ${formatMoney(amount)} y en caja hay ${formatMoney(expectedCashInDrawer)}. ¿Lo registrás igual?`,
          confirmLabel: 'Registrar',
        });
        if (!ok) return;
      }

      proceed();
    },
    [canEdit, branchId, organizationId, activeCashSessionId, confirm, expectedCashInDrawer, runSubmit]
  );

  return {
    submitting: internalSubmitting,
    handleSubmit,
  };
}
